import { CLIENT_RECORD_MAC_ID_ANCHOR } from '@/lib/clients/client-record-zone-d-notices';
import { HOW_WE_CARE_ANCHOR, WHO_TO_CALL_ANCHOR } from '@/lib/clients/person-fact-editors';
import {
  CLIENT_RECORD_DETAILS_ANCHOR,
  remapHiddenProfileTabHref,
} from '@/lib/clients/client-record-profile-tab-policy';
import { clientJourneyHref } from '@/lib/sah/client-care-plan-navigation';
import {
  sahBudgetFundingHref,
  sahBudgetFundingSetupHref,
  sahCarePlanStartHref,
  type UnifiedSahActionNavigation,
} from '@/lib/sah/unified-sah-action-queue';

export { sahBudgetFundingHref, sahBudgetFundingSetupHref, sahCarePlanStartHref };
import { sahReadinessGateHref } from '@/lib/sah/sah-readiness-gate-href';
import type { SahReadinessCheckKey } from '@statura/utils';

/** Legacy SAH hub href patterns that must not appear in orchestrated surfaces. */
export const SAH_FORBIDDEN_HREF_PATTERNS: readonly RegExp[] = [
  // Raw legacy forms AND their panelized variants are both banned — the
  // panel-param class fix (2026-07-07) must not re-admit legacy hub hrefs.
  /\?tab=sah-hub#budget\b/,
  /\?tab=sah-hub#funding\b/,
  /\?tab=sah-hub&panel=budget#budget\b/,
  /\?tab=sah-hub&panel=funding#funding\b/,
  /\/clients\/[^/\n]+\/care-plans(?!\/[^/\n]+\/signed-pdf)/,
];

export const SAH_ORCHESTRATION_SURFACE_FILES = [
  'src/lib/sah/unified-sah-action-queue.ts',
  'src/lib/sah/sah-readiness-gate-href.ts',
  'src/lib/sah/sah-client-journey.ts',
  'src/lib/sah/workflow-context-sah-actions.ts',
  'src/lib/clients/client-record-zone-d-notices.ts',
  'src/lib/clients/client-record-action-queue.ts',
  'src/lib/task-utils.ts',
  'src/components/clients/ClientStatusHeader.tsx',
  'src/app/(dashboard)/clients/page.tsx',
  'src/app/(dashboard)/clients/[id]/page.tsx',
  'src/app/(dashboard)/home-care/readiness/page.tsx',
  'src/app/(dashboard)/home-care/schedules/page.tsx',
  'src/app/(dashboard)/home-care/budgets/page.tsx',
  'src/app/(dashboard)/home-care/funding-changes/page.tsx',
  'src/app/(dashboard)/home-care/claims/page.tsx',
  'src/app/(dashboard)/home-care/exceptions/page.tsx',
  'src/app/(dashboard)/home-care/service-delivery/page.tsx',
  'src/app/(dashboard)/home-care/reconciliation/page.tsx',
  'src/app/(dashboard)/compliance/audit-program/page.tsx',
  'src/components/sah/SahMonthCloseChecklist.tsx',
  'src/components/sah/CarePlanReadinessRail.tsx',
  'src/components/sah/SahClientPendingFinancialsSection.tsx',
  'src/components/clients/ClientSahManagement.tsx',
  'src/components/clients/SahClientJourneyPanel.tsx',
  'src/components/clients/SahPlanInclusionsSection.tsx',
  'src/components/client-onboarding/ClientOnboardingPackPanel.tsx',
  'src/components/care-plans/CarePlanComposer.tsx',
  'src/components/care-plans/ClinicalContextSection.tsx',
  'src/app/(dashboard)/clients/[id]/_components/ClientCarePlanWorkspaceSection.tsx',
  'src/app/(dashboard)/clients/[id]/_components/ClientClinicalSnapshotTab.tsx',
  'src/app/(dashboard)/clients/[id]/_components/ClientScheduleDeliveryTab.tsx',
  'src/lib/clients/visit-pack.ts',
  'src/components/clients/ClientVisitPackPanel.tsx',
  'src/app/(dashboard)/care-plans/[id]/page.tsx',
] as const;

/** Hub surfaces must build SAH journey URLs via contract helpers, not inline tab literals. */
export const SAH_INLINE_HUB_TAB_LITERAL = /\?tab=sah-hub\b/;

export type SahHrefViolation = {
  href: string;
  pattern: string;
  message: string;
};

export function sahClientHubHref(clientId: string): string {
  return clientJourneyHref(clientId);
}

export function sahClientMacIdHref(clientId: string): string {
  return clientJourneyHref(clientId, {
    tab: 'overview',
    hash: CLIENT_RECORD_MAC_ID_ANCHOR,
  });
}

export function sahClientProfileTabHref(clientId: string): string {
  return sahClientHubHref(clientId);
}

export function sahClientDetailsHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: CLIENT_RECORD_DETAILS_ANCHOR });
}

/** Opens the client edit dialog for service-area allocation (Care team panel is on the same record). */
export function sahClientServiceAreaAllocationHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=sah-hub&edit=details#${CLIENT_RECORD_DETAILS_ANCHOR}`;
}

export function sahClientPeopleTabHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=family`;
}

export function sahClientPeopleHowWeCareHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=family#${HOW_WE_CARE_ANCHOR}`;
}

export function sahClientPeopleWhoToCallHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=family#${WHO_TO_CALL_ANCHOR}`;
}

export function sahClientPeopleSignatoryHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=family&panel=agreement-signatory#agreement-signatory`;
}

export function sahClientPeopleContactsHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=family#people-contacts`;
}

export function sahClientDocumentsRequiredHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=documents#required-evidence`;
}

export function sahClientFinanceHandoffHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=sah-finance-handoff#sah-finance-handoff`;
}

export function sahClientCapSupplementsHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=cap-supplements#cap-supplements`;
}

export function sahClientServicesTabHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=services`;
}

/** In-page jump to the Services recurring fold. Not the series editor. */
export function sahClientServicesRecurringHref(clientId: string): string {
  return `${sahClientServicesTabHref(clientId)}#recurring`;
}

/**
 * Production-certified schedule series editor. Lives on the org launchpad
 * when focused on a client — not invented on roster ⋯, and not the Services
 * read-only recurring fold.
 */
export function sahClientScheduleAuthoringHref(clientId: string): string {
  return `/home-care/schedules?clientId=${encodeURIComponent(clientId)}`;
}

export function sahClientAssessmentsTabHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}/assessments`;
}

export function sahClientAssessmentsOverdueHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=assessments&filter=overdue`;
}

export function sahClientMedicationChartHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}/medications`;
}

export function sahClientClinicalTabHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=clinical`;
}

export function sahClientPalliativePlanHref(clientId: string): string {
  return `${sahClientClinicalTabHref(clientId)}#palliative`;
}

export function sahClientEndOfLifeTransitionHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'evidence-authority' });
}

export function sahClientEndOfLifeBudgetBuilderHref(
  clientId: string,
  eolChangeSetId?: string | null,
): string {
  const base = `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=sah-consult-simulator`;
  const query = eolChangeSetId ? `&eolChangeSetId=${encodeURIComponent(eolChangeSetId)}` : '';
  return `${base}${query}#sah-consult-simulator`;
}

export function sahClientBudgetSimulatorHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=sah-consult-simulator#sah-consult-simulator`;
}

/** Visible HCP → Support at Home funding setup/correction journey on the Budget tab. */
export function sahClientFundingCorrectionHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=funding-correction#funding-correction`;
}

export function sahClientFundingProgramsHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=funding-programs#funding-programs`;
}

/**
 * Client-scoped funding authorisation view reachable by the funding-transition
 * role cohort (including coordinators / case managers / clinical leads), not
 * the settings management surface which is limited to admin/finance roles.
 * Eligible admins still reach Settings → Funding authorisations from that view.
 */
export function clientFundingAuthorisationsHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=authorisations`;
}

export function sahClientDeliveryReadinessHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'sah-delivery-readiness' });
}

export function sahClientJourneyTodayHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'today' });
}

export function sahClientConsentsHref(clientId: string): string {
  return clientJourneyHref(clientId, { tab: 'consent' });
}

export function sahClientReadinessHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'sah-readiness' });
}

export function sahClientDecisionMakersHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'decision-makers' });
}

export function sahClientCreateAgreementHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=sah-hub&action=create-agreement`;
}

export function sahClientWelcomeHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=sah-hub&welcome=1`;
}

export function sahClientEvidenceAuthorityHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'evidence-authority' });
}

export function sahClientOnboardingPackHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'onboarding-pack' });
}

export function sahClientFundingArrangementHref(clientId: string): string {
  // The funding-arrangement rail lives on the BUDGET tab (Wave 1 Slice B) —
  // the sah-hub default stranded the head-provider readiness gate (audit P1).
  return clientJourneyHref(clientId, { tab: 'budget', hash: 'funding-arrangement' });
}

export function sahClientRestorativeCareHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'restorative-care' });
}

export function sahClientBudgetExceptionsHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=exceptions#exceptions`;
}

export function sahClientHcpOpeningHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=hcp-opening#hcp-opening`;
}

export function sahClientPlanFitHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=plan-fit#plan-fit`;
}

export function sahClientBudgetReconciliationHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=budget&panel=reconciliation#reconciliation`;
}

export function sahClientCarePlanWorkspaceHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=care-plans`;
}

export function sahClientCaseNotesHref(clientId: string): string {
  return `/clients/${encodeURIComponent(clientId)}?tab=case-notes`;
}

export function sahClientClinicalRiskHref(clientId: string): string {
  return sahClientClinicalTabHref(clientId);
}

export function sahClientOnboardingDocumentMappingsHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'client-onboarding-document-mappings' });
}

export function sahClientClassificationEditorHref(clientId: string): string {
  return clientJourneyHref(clientId, { hash: 'classification-editor' });
}

export type SahClientOnboardingStepAction = {
  href: string;
  label: string;
  detail: string;
};

function normaliseOnboardingKey(value?: string | null): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function sahClientOnboardingStepAction(
  clientId: string,
  stepKey?: string | null,
  hardGateKey?: string | null,
): SahClientOnboardingStepAction {
  const key = normaliseOnboardingKey(stepKey);
  const gate = normaliseOnboardingKey(hardGateKey);
  const signal = key || gate;

  switch (signal) {
    case 'identity_contacts_preferences':
      return {
        href: sahClientDetailsHref(clientId),
        label: 'Open client details',
        detail: 'Confirm identity, contact details, preferred name, language, and key preferences.',
      };
    case 'sah_eligibility_classification':
      return {
        href: sahClientClassificationEditorHref(clientId),
        label: 'Record classification',
        detail:
          'Record SAH eligibility, classification level, transition status, and contribution basis evidence.',
      };
    case 'service_area_allocated':
      return {
        href: sahClientServiceAreaAllocationHref(clientId),
        label: 'Edit allocation',
        detail:
          'Set the primary service area (Edit client) and assign the care-management owner (Care team panel).',
      };
    case 'contribution_budget_billing_setup':
    case 'sah_contribution_budget_billing_setup':
      return {
        href: sahBudgetFundingSetupHref(clientId),
        label: 'Open funding setup',
        detail:
          'Load the current quarter budget, contribution settings, and billing/funding setup.',
      };
    case 'support_plan_service_mix':
      return {
        href: sahCarePlanStartHref(clientId),
        label: 'Start care plan',
        detail:
          'Open the care-plan start panel, then create or continue the plan and add at least one current service inclusion.',
      };
    case 'service_catalogue_pricing_checked':
      return {
        href: sahClientServicesTabHref(clientId),
        label: 'Open services',
        detail: 'Check service catalogue items, pricing, and recurring service setup.',
      };
    case 'client_service_agreement_template_mapped':
      return {
        href: sahClientOnboardingDocumentMappingsHref(clientId),
        label: 'Map agreement',
        detail: 'Select the service-agreement template used for this client onboarding pack.',
      };
    case 'consents_privacy_authorised_contacts':
    case 'sah_consents_privacy_authorised_contacts':
      return {
        href: sahClientConsentsHref(clientId),
        label: 'Open consents',
        detail: 'Record privacy consent, authorised contacts, and sharing preferences.',
      };
    case 'first_visit_roster_ready':
      return {
        href: sahClientScheduleAuthoringHref(clientId),
        label: 'Open roster setup',
        detail: 'Create the first visit or recurring service schedule from the service inclusions.',
      };
    case 'clinical_risks_escalation_notes':
      return {
        href: sahClientClinicalRiskHref(clientId),
        label: 'Open clinical risks',
        detail: 'Record clinical risks, escalation notes, and worker-facing alerts.',
      };
    case 'portal_document_pack_issued':
      return {
        href: sahClientDocumentsRequiredHref(clientId),
        label: 'Open documents',
        detail: 'Issue or upload the portal document pack and participant-facing documents.',
      };
    case 'week_one_review_go_live_approval':
      return {
        href: sahClientDeliveryReadinessHref(clientId),
        label: 'Review go-live',
        detail: 'Complete the delivery-readiness and first-week review checks.',
      };
    default:
      return {
        href: sahClientOnboardingPackHref(clientId),
        label: 'Open requirement',
        detail: 'Open the onboarding pack and complete the outstanding requirement.',
      };
  }
}

/** Legacy no-op — Profile and Care Plans tabs are restored under Option A IA. */
export function remapSahHiddenCarePlansTabHref(href: string): string {
  return href;
}

/** Remap legacy ?tab=overview targets for SAH Journey-led clients. */
export function remapSahHiddenProfileTabHref(href: string): string {
  return remapHiddenProfileTabHref(href, { sahHubVisible: true, racHubVisible: false });
}

/** Legacy no-op — retained for callers that previously remapped hidden tabs. */
export function remapSahHiddenClientTabHref(href: string): string {
  return href;
}

export type BuildSahUnifiedQueueNavigationInput = {
  servicesTabVisible?: boolean;
  suppressCarePlanReviewQueueItem?: boolean;
};

export function buildSahUnifiedQueueNavigation(
  clientId: string,
  input: BuildSahUnifiedQueueNavigationInput = {},
): UnifiedSahActionNavigation {
  return {
    servicesTabVisible: input.servicesTabVisible,
    budgetFinanceHandoffHref: sahClientFinanceHandoffHref(clientId),
    suppressCarePlanReviewQueueItem: input.suppressCarePlanReviewQueueItem,
  };
}

export function isForbiddenSahHref(href: string): SahHrefViolation | null {
  const trimmed = href.trim();
  for (const pattern of SAH_FORBIDDEN_HREF_PATTERNS) {
    if (pattern.test(trimmed)) {
      return {
        href: trimmed,
        pattern: pattern.source,
        message: 'Use canonical unified-queue or sahReadinessGateHref targets instead.',
      };
    }
  }
  return null;
}

export function canonicalSahHrefContract(clientId: string): Record<string, string> {
  const gates: SahReadinessCheckKey[] = [
    'classification',
    'means_tier',
    'consents',
    'care_plan',
    'care_consent',
    'signatory',
    'service_agreement',
    'budget',
    'hcp_opening',
  ];

  return {
    hub: sahClientHubHref(clientId),
    macId: sahClientMacIdHref(clientId),
    budgetFunding: sahBudgetFundingHref(clientId),
    budgetFundingSetup: sahBudgetFundingSetupHref(clientId),
    budgetExceptions: sahClientBudgetExceptionsHref(clientId),
    hcpOpening: sahClientHcpOpeningHref(clientId),
    planFit: sahClientPlanFitHref(clientId),
    budgetReconciliation: sahClientBudgetReconciliationHref(clientId),
    carePlanStart: sahCarePlanStartHref(clientId),
    carePlanWorkspace: sahClientCarePlanWorkspaceHref(clientId),
    financeHandoff: sahClientFinanceHandoffHref(clientId),
    capSupplements: sahClientCapSupplementsHref(clientId),
    servicesTab: sahClientServicesTabHref(clientId),
    scheduleAuthoring: sahClientScheduleAuthoringHref(clientId),
    assessmentsTab: sahClientAssessmentsTabHref(clientId),
    clinicalTab: sahClientClinicalTabHref(clientId),
    deliveryReadiness: sahClientDeliveryReadinessHref(clientId),
    journeyToday: sahClientJourneyTodayHref(clientId),
    consents: sahClientConsentsHref(clientId),
    sahReadiness: sahClientReadinessHref(clientId),
    decisionMakers: sahClientDecisionMakersHref(clientId),
    evidenceAuthority: sahClientEvidenceAuthorityHref(clientId),
    onboardingPack: sahClientOnboardingPackHref(clientId),
    fundingArrangement: sahClientFundingArrangementHref(clientId),
    restorativeCare: sahClientRestorativeCareHref(clientId),
    createAgreement: sahClientCreateAgreementHref(clientId),
    welcome: sahClientWelcomeHref(clientId),
    schedulesHandoff: `/home-care/schedules?clientId=${clientId}`,
    ...Object.fromEntries(gates.map((gate) => [gate, sahReadinessGateHref(clientId, gate)])),
  };
}

export function validateSahHrefList(hrefs: readonly string[]): SahHrefViolation[] {
  return hrefs
    .map((href) => isForbiddenSahHref(href))
    .filter((violation): violation is SahHrefViolation => violation !== null);
}
